/**
 * Who is signed in, and the token that says so.
 *
 * The identity here is a real JWT with the real claim shape, obtained by a
 * real credential exchange — from the Cognito clinician pool in a deployed
 * build, and from `services/api/local/idp.py` in development, so that the
 * local loop needs no network. `exchange` and `refresh` below are the only
 * two functions that know which; nothing else in the app does.
 *
 * **The ID token is what goes to the API, not the access token.** Cognito puts
 * `custom:accountId` and `custom:role` on the former only, and the API needs
 * both; `infra/stacks/api_stack.py` already implies this by setting the
 * authorizers' `jwt_audience` to the client id, which matches an ID token's
 * `aud` and never an access token's `client_id`.
 *
 * **Tokens live in localStorage**, which means script running on this origin
 * can read them. That is the ordinary trade for a single-page app with no
 * backend of its own, and it is survivable here because the clinician token is
 * good for thirty minutes and carries no authorisation of its own — cohort
 * assignments are re-read from the database on every request. It is still the
 * weakest point of this design, and the DPIA should say so. The alternative —
 * a refresh token in an httpOnly cookie, set by a backend-for-frontend — is a
 * server this product does not currently have.
 */

import { API_BASE_URL, COGNITO_CLIENT_ID, IDP_IS_A_STAND_IN } from '../config';
import {
  CognitoError,
  cognitoChangePassword,
  cognitoRefresh,
  cognitoRevoke,
  cognitoSignIn,
} from '@fatigue/shared';
import { NoPasskeyError, passkeySignIn } from './passkeys';
import type { MemberRole } from '@fatigue/shared';

export interface Session {
  readonly userId: string;
  readonly accountId: string;
  readonly role: MemberRole;
  readonly email: string;
}

interface Tokens {
  readonly idToken: string;
  readonly refreshToken: string;
}

const STORAGE_KEY = 'fatigue.tokens';

/** The pool. Patients sign in to the other one, from the patient app. */
const POOL = 'clinicians';

/**
 * Refresh this far before expiry rather than on the 401.
 *
 * A request that starts nine seconds before expiry can arrive after it, so
 * "not expired yet" is not the same question as "will still be valid when it
 * lands". The 401 path below is the backstop, not the plan.
 */
const REFRESH_MARGIN_MS = 60_000;

/* --------------------------------------------------------------- token store */

function load(): Tokens | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<Tokens>;
    if (typeof parsed.idToken !== 'string' || typeof parsed.refreshToken !== 'string') return null;
    return { idToken: parsed.idToken, refreshToken: parsed.refreshToken };
  } catch {
    // Corrupt storage is treated as signed out rather than breaking the app on
    // load. The cost is one sign-in.
    return null;
  }
}

function store(tokens: Tokens | null): void {
  // Named fields only. A Cognito response also carries an access token,
  // which is never persisted — see `freshAccessToken` below.
  const kept = tokens === null ? null : { idToken: tokens.idToken, refreshToken: tokens.refreshToken };
  if (kept === null) window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
  current = kept;
}

let current: Tokens | null = load();

/* -------------------------------------------------------------------- claims */

interface Claims {
  readonly sub?: unknown;
  readonly exp?: unknown;
  readonly email?: unknown;
  readonly 'custom:accountId'?: unknown;
  readonly 'custom:role'?: unknown;
}

/**
 * Read the claims without verifying the signature.
 *
 * Correct, and worth being explicit about: a client cannot meaningfully verify
 * a token it was handed. The signature is checked by API Gateway before any
 * handler runs, and locally by `dev_server.py`. What the app decodes it for is
 * presentation — whose name is in the corner, which tabs to show — and every
 * one of those decisions is enforced again server-side. Editing the payload in
 * devtools changes the UI and nothing else.
 */
function decode(token: string): Claims | null {
  try {
    const payload = token.split('.')[1];
    if (payload === undefined) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Claims;
  } catch {
    return null;
  }
}

function toSession(idToken: string): Session | null {
  const claims = decode(idToken);
  if (claims === null) return null;

  const role = claims['custom:role'];
  if (
    typeof claims.sub !== 'string' ||
    typeof claims['custom:accountId'] !== 'string' ||
    (role !== 'owner' && role !== 'clinician')
  ) {
    return null;
  }

  return {
    userId: claims.sub,
    accountId: claims['custom:accountId'],
    role,
    email: typeof claims.email === 'string' ? claims.email : '',
  };
}

function expiryOf(token: string): number {
  const exp = decode(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : 0;
}

/* ------------------------------------------------------------------ the IdP */

export class SignInError extends Error {}

/** The local IdP, in development. Its routes exist only on `dev_server.py`. */
async function post(path: string, body: unknown): Promise<Tokens> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = 'Could not sign you in.';
    try {
      const failure = (await response.json()) as { message?: unknown };
      if (typeof failure.message === 'string' && failure.message !== '') message = failure.message;
    } catch {
      // Keep the generic message rather than reporting a parse failure at
      // somebody trying to sign in.
    }
    throw new SignInError(message);
  }

  const tokens = (await response.json()) as Partial<Tokens>;
  if (typeof tokens.idToken !== 'string' || typeof tokens.refreshToken !== 'string') {
    throw new SignInError('The identity provider returned something unusable.');
  }
  return { idToken: tokens.idToken, refreshToken: tokens.refreshToken };
}

function cognitoClientId(): string {
  // IDP_IS_A_STAND_IN is false exactly when this is set.
  return COGNITO_CLIENT_ID ?? '';
}

async function exchange(email: string, password: string): Promise<Tokens> {
  if (IDP_IS_A_STAND_IN) return post('/dev/sign-in', { pool: POOL, email, password });

  try {
    return await cognitoSignIn(cognitoClientId(), email, password);
  } catch (err) {
    if (err instanceof CognitoError && err.type === 'NotAuthorizedException') {
      // Also what an unknown address gets — see `prevent_user_existence_errors`.
      throw new SignInError('That email address and password do not match.');
    }
    if (err instanceof CognitoError && err.type !== 'NetworkError') {
      throw new SignInError(err.message);
    }
    throw err;
  }
}

async function refresh(refreshToken: string): Promise<Tokens> {
  if (IDP_IS_A_STAND_IN) return post('/dev/refresh', { refreshToken });

  try {
    return await cognitoRefresh(cognitoClientId(), refreshToken);
  } catch (err) {
    // An expired or revoked refresh token. `data/api.ts` answers a SignInError
    // by signing out, which is the right end for both.
    if (err instanceof CognitoError && err.type !== 'NetworkError') {
      throw new SignInError(err.message);
    }
    throw err;
  }
}

/* ------------------------------------------------------------------- public */

/** The signed-in clinician, or null. Null is the signed-out state, not an error. */
export function session(): Session | null {
  return current === null ? null : toSession(current.idToken);
}

export async function signIn(email: string, password: string): Promise<Session> {
  const tokens = await exchange(email, password);

  const next = toSession(tokens.idToken);
  if (next === null) {
    // A token without an account id or a recognised role is one this app
    // cannot act on. Refusing it here beats a half-signed-in state that fails
    // later with something less explicable.
    throw new SignInError('That login is not set up for the clinician dashboard.');
  }

  store(tokens);
  return next;
}

/**
 * Sign in with a passkey instead of a password. The address comes first —
 * Cognito needs to know whose passkeys to ask the browser for.
 */
export async function signInWithPasskey(email: string): Promise<Session> {
  let tokens: Tokens;
  try {
    tokens = await passkeySignIn(cognitoClientId(), email.trim());
  } catch (err) {
    if (err instanceof NoPasskeyError) {
      throw new SignInError(
        'No passkey is set up for that address yet. Sign in with your password, then add one in Settings.',
      );
    }
    if (err instanceof CognitoError && err.type === 'NotAuthorizedException') {
      throw new SignInError('That passkey was not accepted. Try your password instead.');
    }
    if (err instanceof Error && err.name === 'NotAllowedError') {
      // The person cancelled the browser's prompt, or it timed out.
      throw new SignInError('Passkey sign-in was cancelled.');
    }
    throw new SignInError('Could not sign in with a passkey. Try your password instead.');
  }

  const next = toSession(tokens.idToken);
  if (next === null) throw new SignInError('That login is not set up for the clinician dashboard.');
  store(tokens);
  return next;
}

/**
 * An access token for Cognito's own self-service calls, obtained when one is
 * needed and never stored: it is the token that can change this person's
 * password and passkeys, so it should not sit in localStorage beside the rest.
 */
export async function freshAccessToken(): Promise<string> {
  const tokens = current;
  if (tokens === null || IDP_IS_A_STAND_IN) throw new SignInError('Signed out.');
  try {
    return (await cognitoRefresh(cognitoClientId(), tokens.refreshToken)).accessToken;
  } catch (err) {
    if (err instanceof CognitoError && err.type === 'NetworkError') {
      throw new SignInError('Could not reach the sign-in service. Check your connection.');
    }
    // An expired or revoked session. Cognito's own wording is not for people.
    throw new SignInError('Your session has ended. Sign out and sign in again to continue.');
  }
}

/**
 * Ask for the password again and return a fresh access token from it.
 *
 * Used before adding a passkey: a session left open on somebody's desk should
 * not be enough to plant a passkey that outlives it. It is a check in this
 * client, not a server rule — Cognito accepts any valid access token — but it
 * is the case that matters, a person at an unattended screen.
 */
export async function confirmPassword(password: string): Promise<string> {
  const email = session()?.email ?? '';
  try {
    return (await cognitoSignIn(cognitoClientId(), email, password)).accessToken;
  } catch (err) {
    if (err instanceof CognitoError && err.type === 'NotAuthorizedException') {
      throw new SignInError('That password is not right.');
    }
    throw new SignInError('Could not check your password. Try again in a moment.');
  }
}

export async function changePassword(previous: string, proposed: string): Promise<void> {
  try {
    await cognitoChangePassword(await freshAccessToken(), previous, proposed);
  } catch (err) {
    if (err instanceof CognitoError && err.type === 'NotAuthorizedException') {
      throw new SignInError('Your current password is not right.');
    }
    if (err instanceof CognitoError && err.type === 'InvalidPasswordException') {
      throw new SignInError('The new password needs at least 12 characters.');
    }
    if (err instanceof CognitoError && err.type === 'LimitExceededException') {
      throw new SignInError('Too many attempts. Wait a few minutes and try again.');
    }
    if (err instanceof SignInError) throw err;
    throw new SignInError('Could not change your password. Try again in a moment.');
  }
}

export function signOut(): void {
  const signingOut = current;
  if (signingOut !== null && !IDP_IS_A_STAND_IN) {
    // Best effort, and not awaited: the reload below must not wait on the
    // network, and a revocation that fails leaves a refresh token that is
    // gone from this device and expires within hours anyway.
    void cognitoRevoke(cognitoClientId(), signingOut.refreshToken).catch(() => undefined);
  }
  store(null);
  // A full reload rather than a re-render. Every cache in the app is scoped to
  // the signed-in person, and reloading is the only thing that certainly
  // leaves no roster from the previous one on screen.
  window.location.reload();
}

/** In flight, so that ten parallel requests hitting expiry cause one refresh. */
let refreshing: Promise<Tokens> | null = null;

async function refreshTokens(): Promise<Tokens> {
  const tokens = current;
  if (tokens === null) throw new SignInError('Signed out.');

  refreshing ??= refresh(tokens.refreshToken)
    .then((next) => {
      store(next);
      return next;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

/**
 * The token to send, refreshed first if it is about to expire.
 *
 * Throws when there is nothing to refresh with — the caller's answer to that
 * is to sign the person out, which is what `data/api.ts` does.
 */
export async function idToken(): Promise<string> {
  const tokens = current;
  if (tokens === null) throw new SignInError('Signed out.');

  if (Date.now() < expiryOf(tokens.idToken) - REFRESH_MARGIN_MS) return tokens.idToken;
  return (await refreshTokens()).idToken;
}

/** Forces the next `idToken()` to refresh. Called after an unexpected 401. */
export async function renew(): Promise<string> {
  return (await refreshTokens()).idToken;
}
